import { useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminUserQuotaActionsPanel } from '@/components/admin-user-quota/admin-user-quota-actions-panel';
import { AdminUserQuotaConfigForm } from '@/components/admin-user-quota/admin-user-quota-config-form';
import { AdminUserQuotaHeader } from '@/components/admin-user-quota/admin-user-quota-header';
import { AdminUserQuotaHistoryTabs } from '@/components/admin-user-quota/admin-user-quota-history-tabs';
import { AdminUserQuotaStatusCard } from '@/components/admin-user-quota/admin-user-quota-status-card';
import { getQuotaErrorMessage } from '@/features/quota/quota-errors';
import { useAdminUserQuotaDetail } from '@/features/quota/useAdminUserQuota';

function QueryError({ message }: { message: string }) {
  return (
    <section className="rounded-[var(--radius-surface-2xl)] border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
      {message}
    </section>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-24 w-full rounded-[var(--radius-surface-3xl)]" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={`quota-kpi-${index}`} className="h-32 rounded-[var(--radius-surface-3xl)]" />
        ))}
      </div>
      <Skeleton className="h-96 w-full rounded-[var(--radius-surface-4xl)]" />
    </div>
  );
}

export function AdminUserQuotaPage() {
  const { userId } = useParams<{ userId: string }>();
  const detailQuery = useAdminUserQuotaDetail(userId);

  if (!userId) {
    return <QueryError message="Identifiant utilisateur manquant." />;
  }

  if (detailQuery.isLoading) {
    return <PageSkeleton />;
  }

  if (detailQuery.isError || !detailQuery.data) {
    return <QueryError message={getQuotaErrorMessage(detailQuery.error)} />;
  }

  const detail = detailQuery.data;

  return (
    <section className="space-y-8 text-foreground" data-testid="admin-user-quota-page">
      <AdminUserQuotaHeader detail={detail} />
      <AdminUserQuotaStatusCard demoExpiresAt={detail.user.demoExpiresAt} status={detail.status} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <AdminUserQuotaConfigForm detail={detail} userId={userId} />
        <AdminUserQuotaActionsPanel detail={detail} userId={userId} />
      </div>
      <AdminUserQuotaHistoryTabs userId={userId} />
    </section>
  );
}
